import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Vérifier si l'utilisateur doit changer son mot de passe
      if (response.data.doit_changer_mdp) {
        setMustChangePassword(true);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Vérifier si l'utilisateur doit changer son mot de passe
      if (userData.doit_changer_mdp) {
        setMustChangePassword(true);
      }
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API_URL}/api/auth/register`, userData);
      return await login(userData.email, userData.password);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const onPasswordChanged = () => {
    setMustChangePassword(false);
    // Mettre à jour l'utilisateur
    if (user) {
      setUser({ ...user, doit_changer_mdp: false });
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    mustChangePassword,
    onPasswordChanged
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};